import { readonly, ref, type DeepReadonly, type Ref } from 'vue';

export type PromptTone = 'default' | 'danger';
export type PromptInputType = 'text' | 'number';

export interface PromptOptions {
  readonly title?: string;
  readonly message?: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly initial?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: PromptTone;
  readonly inputType?: PromptInputType;
  readonly required?: boolean;
  readonly validate?: (value: string) => string | null;
}

export interface PromptRequest extends PromptOptions {
  readonly id: number;
}

const current = ref<PromptRequest | null>(null);
let resolver: ((value: string | null) => void) | null = null;
let nextId = 0;

function settle(value: string | null): void {
  const resolve = resolver;
  resolver = null;
  current.value = null;
  if (resolve !== null) {
    resolve(value);
  }
}

function ask(options: PromptOptions): Promise<string | null> {
  settle(null);
  nextId += 1;
  current.value = { id: nextId, ...options };
  return new Promise<string | null>((resolve) => {
    resolver = resolve;
  });
}

export interface PromptApi {
  readonly request: DeepReadonly<Ref<PromptRequest | null>>;
  ask(options: PromptOptions): Promise<string | null>;
  submit(value: string): void;
  cancel(): void;
}

export function usePrompt(): PromptApi {
  return {
    request: readonly(current),
    ask,
    submit: (value: string) => settle(value),
    cancel: () => settle(null),
  };
}
